import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import { CommonErrorHelperTextV1, CommonSuccessAlertConfirmation } from 'gemlux-common-components';
import { useNavigate } from 'react-router-dom';
import { styled as materialStyled } from '@mui/material/styles';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import LogoIcon from '../../../assets/img/sidebar/logo.svg';
import { theme } from '../../../theme/theme';
import { SquareInputBox } from '../../../components/SquareInputBox/SquareInputBox';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { userActions } from '../../../redux/user/userSlice';
import Logo from '../../../assets/img/logo-large.svg';
import CustomLoader from '../../../components/CustomLoader/CustomLoader';
// eslint-disable-next-line max-len

const CustomGridContainer = materialStyled(Grid)({
  maxWidth: '380px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: '1px solid #DCE0E5',
  boxShadow: '0px 1px 2px 0px rgba(128, 128, 128, 0.12)',
  padding: '24px 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const CustomButton = materialStyled(Button, {
  shouldForwardProp: (prop) => prop !== 'plain',
})<{ plain: boolean }>(({ plain }) => ({
  backgroundColor: plain ? 'transparent' : '#3348FF',
  border: '1px solid #DCE0E5',
  width: '120px',
  borderRadius: '8px',
  padding: '8px 12px',
  color: plain ? '#14181F' : '#fff',
  boxShadow: plain
    ? ''
    : '0px 12px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px -2px 2px 0px rgba(48, 48, 48, 0.10) inset',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: plain ? '#f5f5f7' : '#3030c9',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgb(159, 173, 207)',
    color: 'rgba(0, 0, 0, 0.26)',
    border: 'none',
    boxShadow: 'none',
  },
}));

const CreatePin = () => {
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(4).fill(''));
  const [disableCreatePin, setDisableCreatePin] = useState<boolean>(true);
  const inputRefs = useRef<HTMLInputElement[]>(Array(4).fill(null));
  const keyMap: number[] = [0, 1, 2, 3];

  const { isPinSkipped, componentStatus } = useAppSelector((state: RootState) => state.user);
  const error = _.get(componentStatus, 'error', undefined);
  const isPinSetting = Boolean(_.get(componentStatus, 'isPinSetting', false));
  const successModalOpen = Boolean(_.get(componentStatus, 'successModalOpen', false));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };
  const handleCreatePin = () => {
    const pin = verificationCode.join('');
    dispatch(userActions.updateUserPinAsync({ pin }));
  };
  const handleSkipClick = () => {
    dispatch(userActions.updateUserPinAsync({ pin: null }));
    navigate(`/my-gem-vault`);
  };
  const handleDismiss = () => {
    navigate(`/my-gem-vault`);
  };

  const handleVerificationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    if (!Number.isNaN(Number(event.target.value)) && index < 4) {
      const code = [...verificationCode];
      code[index] = event.target.value.slice(-1);
      setVerificationCode(code);

      if (event.target.value !== '' && index < 3) {
        inputRefs.current[index + 1].focus();
      }
      if (_.compact(code).length === 4 && index === 3) {
        setDisableCreatePin(false);
      }
      if (_.compact(code).length < 4) {
        setDisableCreatePin(true);
      }
    }
  };

  useEffect(() => {
    if (isPinSkipped) {
      return navigate('/my-gem-vault');
    }
  }, []);

  if (isPinSetting && !isPinSkipped) return <CustomLoader />;

  if (!isPinSetting && successModalOpen)
    return (
      <>
        <Grid position="absolute" top="20px" left="0" width="100%" display="flex" justifyContent="center">
          <Grid component="img" src={Logo} />
        </Grid>
        <CommonSuccessAlertConfirmation confirmText="Dismiss" handleClick={handleDismiss} title="Pin Created!" />
      </>
    );

  if (!isPinSetting && !successModalOpen && !isPinSkipped)
    return (
      <CustomGridContainer container gap="40px">
        <Grid item component="img" src={LogoIcon} />

        <Grid item xs={12} sm={12} display="flex" flexDirection="column">
          <Grid item textAlign="center">
            <Stack spacing={1} mb="24px">
              <Typography variant="textH5">Create a PIN</Typography>
              <Typography variant="textBase" color="#525E6F">
                Create a PIN to use with your KRISTA/KROMA device. You can create/change this later from the settings
                page.
              </Typography>
            </Stack>
          </Grid>
          <Grid container gap={1.5} display="flex" flexDirection="column" textAlign="center">
            <Box display="flex" columnGap={3} justifyContent="center">
              {verificationCode.map((e, index) => (
                <SquareInputBox
                  theme={theme}
                  height="40px"
                  width="40px"
                  autoComplete="off"
                  inputRef={(el) => (inputRefs.current[index] = el as HTMLInputElement)}
                  autoFocus={index === 0}
                  onKeyDown={(key) => handleKeyDown(index, key)}
                  key={keyMap[index]}
                  type="text"
                  name={`n${index}`}
                  value={e}
                  onChange={(event) => {
                    handleVerificationCodeChange(event, index);
                  }}
                  variant="outlined"
                />
              ))}
            </Box>{' '}
            {error && (
              <Grid item xs={12} sm={12} justifyContent="center" display="flex">
                <CommonErrorHelperTextV1 errorText={String(error)} />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} display="flex" justifyContent="space-between">
          <CustomButton plain onClick={handleSkipClick}>
            {' '}
            <Typography variant="textBase500">Skip</Typography>
          </CustomButton>
          <CustomButton plain={false} onClick={handleCreatePin} disabled={disableCreatePin}>
            {' '}
            <Typography variant="textBase500">Create PIN</Typography>
          </CustomButton>
        </Grid>
      </CustomGridContainer>
    );
};

export default CreatePin;
